import { Bounds, ReadonlyBounds } from '@tencent/alloy-renderer';
import { BrowserCanvas } from '@tencent/alloy-renderer-browser';
import { IComponentWrapper } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-wrapper.interface';
import {
    calculateFrame, calculateUiModelFrame, changeVisibleStatus,
} from '@src/slide/application/service/similarity-collection/utils';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import {
    ISimilarityCollectionServiceDelegates,
} from '@src/slide/application/service/similarity-collection/similarity-collection-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import type { RendererShapeComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-shape-component';
import type { RendererTableComponent } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/renderer-table-component';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { getEditorSelectableAreaRect } from '@tencent/alloy-editor';
import { UIState } from '@src/slide/infrastructure/module/independent/ui-state';

export class ShapeRenderer {
    protected slideLeft: number;
    protected slideTop: number;
    private renderComponents: IComponentWrapper;
    private readonly services: ISimilarityCollectionServiceDelegates;
    public constructor(services: ISimilarityCollectionServiceDelegates, renderComponents: IComponentWrapper) {
        this.services = services;
        this.renderComponents = renderComponents;
        const { slideLeft, slideTop } = this.services.editAreaCanvasSizeManager.getCanvasSize();
        this.slideLeft = slideLeft;
        this.slideTop = slideTop;
    }

    public collectData() {
        const target = (this.renderComponents.wrapperInstance?.children?.[0] as RendererShapeComponent)?.baseComponent
            || this.renderComponents;
        // office获取到的都是原始数据，不包含旋转、边框、箭头等等
        // 而tdoc如果用calculateFrame则获取到的是所有变换完成渲染后的盒模型
        // 两者对齐比较麻烦，暂时先用数据对齐
        const frame = calculateUiModelFrame(target.factoryOptions.model.props.style);
        return {
            type: this.type,
            textRange: this.textRange,
            table: this.table,
            ...frame,
        };
    }

    public collectImage() {
        return {
            image: this.export(),
        };
    }

    public get type(): string {
        return this.renderComponents.factoryOptions.model.type;
    }

    public get textRange() {
        if (!this.hasText) {
            return;
        }
        const { textEditor } = this.renderComponents.wrapperInstance.children[0] as RendererShapeComponent;
        const worldBounds = getEditorSelectableAreaRect(textEditor?.editor);
        return calculateFrame(worldBounds as unknown as ReadonlyBounds, this.slideLeft, this.slideTop);
    }

    public get table() {
        if (!this.hasTable) {
            return;
        }

        const cellsInfo: any[] = [];
        const tableBackground = (this.renderComponents.wrapperInstance.children[0] as RendererTableComponent)
            .getTableBackgroundComponent();
        const model = this.renderComponents.factoryOptions.model as ITableModel;

        const rowCount = model.tableItems.length;
        const columnCount = model.tableItems[0].length;

        model.tableItems.forEach((rowInfo, rowIndex) => {
            rowInfo.forEach((cell, cellIndex) => {
                const cellWrapper = tableBackground?.getBackgroundCellByRowCol(rowIndex, cellIndex);
                if (cellWrapper) {
                    cellsInfo.push(calculateFrame(cellWrapper.worldBounds, this.slideLeft, this.slideTop));
                } else if (cell.hMerge) {
                    cellsInfo.push(cellsInfo[(rowIndex * columnCount) + (cellIndex - 1)]);
                } else if (cell.vMerge) {
                    cellsInfo.push(cellsInfo[((rowIndex - 1) * columnCount) + cellIndex]);
                } else {
                    cellsInfo.push(undefined);
                }
            });
        });

        return {
            rowCount,
            columnCount,
            cells: cellsInfo,
        };
    }

    public get hasText(): boolean {
        const { paragraphList } = this.renderComponents.factoryOptions.model as IShapeModel;
        const isEmpty = !paragraphList || paragraphList.length < 1
            || (paragraphList.length === 1 && paragraphList[0]?.textRuns?.length === 0);
        return !isEmpty;
    }

    public get hasTable(): boolean {
        return this.renderComponents.factoryOptions.model.type === UIModelType.TABLE;
    }

    public export(type?: string) {
        const canvasElement = document.createElement('canvas');
        const bounds = new Bounds();
        const canvas = new BrowserCanvas({
            native: canvasElement,
            bindEvents: false,
        });

        bounds.addBounds(this.renderComponents.filterWorldBounds);
        canvas.width = Math.round(bounds.width);
        canvas.height = Math.round(bounds.height);
        const originalX = this.renderComponents.x;
        const originalY = this.renderComponents.y;
        const { scale } = this.services.editAreaCanvasSizeManager.getCanvasSize();
        UIState.isExportingElementImage = true;
        this.renderComponents.x += -bounds.x / scale;
        this.renderComponents.y += -bounds.y / scale;
        changeVisibleStatus([this.renderComponents], true);

        this.renderComponents.render(canvas);

        this.renderComponents.x = originalX;
        this.renderComponents.y = originalY;
        changeVisibleStatus([this.renderComponents], true);
        UIState.isExportingElementImage = false;

        return canvas.toDataUrl(type);
    }
}
